/* Systemet som figur, i seks visninger: Innboks (forespørselen kommer inn), Arbeid (alle saker), Saken (MS Nordlys fra kalkyle til
   kontrollert tilbud), Nytt arbeid (assistenten), Plan & kapasitet og Hjem. Samme skall som appen, «MS Nordlys» går igjennom alt.
   Spiller av selv; et klikk på en fane tar over. */
import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarRange, Home, Inbox as InboxIcon, MessagesSquare, Sparkles, Target } from "lucide-react";
import { Inert, Stage } from "../Stage";
import { useCountUp } from "../motion";
import { Shell, WorkPanel } from "./LosShell";
import { LosSak, SCRIPT } from "./LosSak";
import { IntakeStill } from "./LosIntake";
import { HomeView, InboxView, PlanView, WorkView } from "./LosViews";
import { CASE } from "./losMockData";

/* ------------------------------------------------------------------ manuset */

const INBOX_BEATS = [
  { ms: 5200, toast: ["Ny forespørsel lest av assistenten", `${CASE.customer} · ${CASE.vessel} · 28 arbeidsposter, 5 avklaringer · frist 20. nov`] },
  { ms: 5600, toast: [`Saken «${CASE.name}» er opprettet`, "Arbeidspostene fra spesifikasjonen er lagt inn – ingenting skrevet på nytt"] },
];
const WORK_BEATS = [
  { ms: 4600, toast: null },
  { ms: 5400, toast: ["MF Vågsøy – 1 avklaring venter svar", "Torghatten Nord · tilbudsfrist om 3 dager · påminnelse foreslått"] },
];
const SAK_BEATS = SCRIPT.map((b) => ({ ms: b.ms, toast: b.toast || null }));
const NY_BEATS = [
  { ms: 3400, at: -1, toast: null },
  { ms: 4200, at: 0, toast: null },
  { ms: 5600, at: 1, toast: ["28 arbeidsposter og 5 avklaringer funnet", "Fra 114 sider spesifikasjon – kundens tekst beholdt urørt"] },
  { ms: 4600, at: 2, toast: ["Arbeidet er opprettet", `#${CASE.number} ${CASE.vessel} · 28 poster · 3 dokumenter · klar for kalkyle`] },
];
const PLAN_BEATS = [
  { ms: 5000, toast: null },
  { ms: 5800, toast: ["Uke 12–14 kan loves for MS Nordlys", "Flytedokk ledig · Maskin 92 % i uke 13 – reservasjon lagt inn til 1. des"] },
];
const HOME_BEATS = [
  { ms: 5000, toast: null },
  { ms: 5400, toast: ["Tre tilbud venter på svar", "Redningsselskapet, Frøy og Sølvtrans – oppfølging foreslått"] },
];

const HOST = "workspace.los-marine.no";
export const VIEWS = [
  { key: "innboks", url: `${HOST}/innboks`, Icon: InboxIcon, beats: INBOX_BEATS, active: "innboks", title: "Innboks" },
  { key: "arbeid", url: `${HOST}/arbeid`, Icon: MessagesSquare, beats: WORK_BEATS, active: "arbeid", title: "Arbeid" },
  { key: "sak", url: `${HOST}/p/${CASE.number}`, Icon: Target, beats: SAK_BEATS, active: "arbeid", title: "Arbeid" },
  { key: "ny", url: `${HOST}/ny`, urlAt: `${HOST}/ny/8f3a`, Icon: Sparkles, beats: NY_BEATS, active: "arbeid", title: "Nytt arbeid" },
  { key: "plan", url: `${HOST}/plan`, Icon: CalendarRange, beats: PLAN_BEATS, active: "plan", title: "Plan" },
  { key: "hjem", url: `${HOST}/`, Icon: Home, beats: HOME_BEATS, active: "hjem", title: "Hjem" },
];

const MANUAL_MS = 30000;

/* ett slag om gangen mens figuren er i bildet; når en visning er ferdig går vi videre (eller går i ring mens leseren har valgt den) */
const useScript = (active) => {
  const [st, setSt] = useState({ v: 0, b: 0, loop: 0 });
  const [paused, setPaused] = useState(false);
  const manualUntil = useRef(0);
  useEffect(() => {
    if (!active || paused) return undefined;
    const t = setTimeout(() => {
      setSt((s) => {
        const beats = VIEWS[s.v].beats;
        if (s.b + 1 < beats.length) return { ...s, b: s.b + 1 };
        if (manualUntil.current > Date.now()) return { v: s.v, b: 0, loop: s.loop + 1 };
        return { v: (s.v + 1) % VIEWS.length, b: 0, loop: s.loop + 1 };
      });
    }, VIEWS[st.v].beats[st.b].ms);
    return () => clearTimeout(t);
  }, [st, active, paused]);
  const select = useCallback((i) => {
    manualUntil.current = Date.now() + MANUAL_MS;
    setSt((s) => ({ v: i, b: 0, loop: s.loop + 1 }));
  }, []);
  return { ...st, paused, setPaused, select };
};

/* ------------------------------------------------------------------ appen rundt visningene */

const App = ({ view, beat, mode, active }) => {
  const v = VIEWS[view];
  const b = v.beats[beat] || v.beats[0];
  const root = useRef(null);
  useCountUp(root, active, { delay: 500 }, [view, beat]);
  const panel = v.key === "sak" || v.key === "ny" ? <WorkPanel activeName={v.key === "sak" || b.at >= 2 ? CASE.name : "__none__"} /> : null;
  return (
    <div ref={root} className="h-full w-full">
      <Shell mode={mode} active={v.active} panel={panel} title={v.title} toast={b.toast} testId="los-mock-system" view={v.key} beat={beat}>
        <Inert className="h-full">
          <div key={v.key} className="mb-view-enter h-full min-h-0">
            {v.key === "innboks" ? <InboxView beat={beat} mode={mode} /> : v.key === "arbeid" ? <WorkView beat={beat} mode={mode} /> : v.key === "sak" ? <LosSak beat={{ ...SCRIPT[beat], toast: null }} tab={SCRIPT[beat].tab} mode={mode} /> : v.key === "ny" ? <IntakeStill at={b.at} mode={mode} /> : v.key === "plan" ? <PlanView beat={beat} mode={mode} /> : <HomeView beat={beat} mode={mode} />}
          </div>
        </Inert>
      </Shell>
    </div>
  );
};

/* ------------------------------------------------------------------ figuren: faner · ramme · bildetekst */

const Tabs = ({ views, current, loop, paused, onSelect }) => {
  const refs = useRef([]);
  const strip = useRef(null);
  useEffect(() => {
    const el = refs.current[current];
    const s = strip.current;
    if (!el || !s || s.scrollWidth <= s.clientWidth + 1) return;
    const left = el.offsetLeft - (s.clientWidth - el.offsetWidth) / 2;
    s.scrollTo({ left: Math.max(0, Math.min(left, s.scrollWidth - s.clientWidth)), behavior: "smooth" });
  }, [current]);
  const onKey = (e, i) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const n = (i + (e.key === "ArrowRight" ? 1 : -1) + VIEWS.length) % VIEWS.length;
    onSelect(n);
    refs.current[n]?.focus();
  };
  return (
    <div ref={strip} className="kvl-switch" role="tablist" aria-label="Visning" data-paused={paused ? "true" : "false"} data-testid="tilbod-system-tabs">
      {VIEWS.map((v, i) => {
        const meta = views[i] || {};
        const on = i === current;
        const dur = v.beats.reduce((s, b) => s + b.ms, 0);
        return (
          <button key={v.key} ref={(el) => { refs.current[i] = el; }} type="button" role="tab" aria-selected={on} tabIndex={on ? 0 : -1} className="kvl-switch-tab" onClick={() => onSelect(i)} onKeyDown={(e) => onKey(e, i)} data-testid={`tilbod-system-tab-${v.key}`}>
            <span className="kvl-switch-ic"><v.Icon aria-hidden="true" /></span>
            <span className="kvl-switch-txt"><b>{meta.label || v.key}</b>{meta.hint && <small>{meta.hint}</small>}</span>
            {on && <i key={`${v.key}-${loop}`} className="kvl-switch-bar" style={{ "--dur": `${dur}ms` }} aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
};

export const SystemMockup = ({ d }) => {
  const [active, setActive] = useState(false);
  const { v, b, loop, paused, setPaused, select } = useScript(active);
  const views = d?.idea?.views || [];
  const meta = views[v] || {};
  const url = VIEWS[v].urlAt && (VIEWS[v].beats[b]?.at ?? -1) >= 0 ? VIEWS[v].urlAt : VIEWS[v].url;
  return (
    <figure className="kvl-figure kvl-figure-mock" data-testid="tilbod-idea-figure" data-view={VIEWS[v].key}>
      <Tabs views={views} current={v} loop={loop} paused={paused || !active} onSelect={select} />
      <div className="kvl-frame" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <Stage url={url} caption={d?.ui?.prototype} height={820} narrowHeight={640} widths={{ fullWidth: 1500 }} testId="tilbod-system-mockup" attrs={{ "data-view": VIEWS[v].key, "data-beat": b }} onActive={setActive}>
          {({ mode, active: on }) => <App view={v} beat={b} mode={mode} active={on} />}
        </Stage>
      </div>
      <figcaption key={VIEWS[v].key} className="kvl-cap-enter" data-testid="tilbod-idea-caption">{meta.caption || d?.idea?.caption}</figcaption>
    </figure>
  );
};
